import { stripRichMarkup, type BoardNode } from '@kanvas/board-core';
import { useMemo, useState } from 'react';
import type { ActionContext } from '../actions/actions';
import { useMyVotes, useNodes, useVoteSession, useVoteTotals } from '../collab/hooks';
import { Icon } from './icons';
import { PanelDivider, Stepper } from './controls';

/** Popover that starts, monitors and ends a dot-voting session. */
export function VotingPanel({ ctx }: { ctx: ActionContext }) {
  const { doc, identity } = ctx;
  const session = useVoteSession(doc);
  const totals = useVoteTotals(doc);
  const nodes = useNodes(doc);
  const mine = useMyVotes(doc, identity.userId);
  const used = useMemo(() => Object.values(mine).reduce((s, n) => s + n, 0), [mine]);
  const [max, setMax] = useState(session.max);
  const canEdit = doc.canEdit();

  const results = useMemo(() => {
    const byId = new Map(nodes.map((n) => [n.id, n]));
    return [...totals.entries()]
      .map(([id, count]) => ({ node: byId.get(id), count }))
      .filter((r) => r.node)
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [totals, nodes]);

  return (
    <div className="popover-stack voting-panel">
      {!session.active ? (
        canEdit ? (
          <>
            <p className="popover-hint">
              Каждый участник получит ограниченное число голосов и сможет отдать их за
              стикеры, фигуры или карточки прямо на доске.
            </p>
            <Stepper value={max} min={1} max={20} suffix="голосов" onChange={setMax} />
            <button
              type="button"
              className="primary-btn"
              onClick={() => doc.setVoteSession({ active: true, max })}
            >
              <Icon name="vote" size={16} /> Начать голосование
            </button>
            {totals.size > 0 && (
              <button type="button" className="menu-item" onClick={() => doc.clearVotes()}>
                <Icon name="trash" size={16} /> Очистить прошлые результаты
              </button>
            )}
          </>
        ) : (
          <p className="popover-hint">Сейчас нет активного голосования.</p>
        )
      ) : (
        <>
          <div className="voting-status">
            <span>
              Ваши голоса: <strong>{used} / {session.max}</strong>
            </span>
            <span className="popover-hint">Кликните «+» на бейджике объекта, чтобы отдать голос.</span>
          </div>
          <PanelDivider />
          {canEdit && (
            <button
              type="button"
              className="primary-btn"
              onClick={() => doc.setVoteSession({ active: false })}
            >
              Завершить голосование
            </button>
          )}
        </>
      )}

      {results.length > 0 && (
        <>
          <PanelDivider />
          <div className="popover-label">Результаты</div>
          <div className="voting-results">
            {results.map(({ node, count }) => (
              <div key={node!.id} className="voting-result-row">
                <span className="voting-result-count">{count}</span>
                <span className="voting-result-label">{describeNode(node!)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function describeNode(node: BoardNode): string {
  const text = 'text' in node ? stripRichMarkup(node.text) : undefined;
  if (typeof text === 'string' && text.trim() !== '') return text.trim().slice(0, 40);
  const labels: Record<string, string> = {
    shape: 'Фигура',
    sticky: 'Стикер',
    text: 'Текст',
    image: 'Изображение',
    draw: 'Рисунок',
    embed: 'Встраивание',
  };
  return labels[node.type] ?? node.type;
}
